import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  Alert, CircularProgress, Tabs, Tab, Divider,
} from '@mui/material';
import { Pets as PetsIcon } from '@mui/icons-material';
import { useClientPortalStore } from '../stores/clientPortalStore';
import { portalApi } from '../services/portalApiClient';
import { API_ENDPOINTS } from '../config/api';

const DEFAULT_SITE_ID = parseInt(import.meta.env.VITE_DEFAULT_SITE_ID ?? '1', 10);

const ClientLoginPage = () => {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useClientPortalStore();
  const [tab, setTab] = useState(0);

  // Login form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Register form
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regClientId, setRegClientId] = useState('');
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState(false);
  const [regLoading, setRegLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await login(email, password, DEFAULT_SITE_ID);
      navigate('/client-portal');
    } catch { /* error shown from store */ }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegError('');
    if (!regEmail || !regPassword || !regClientId) { setRegError('All fields are required'); return; }
    if (regPassword.length < 8) { setRegError('Password must be at least 8 characters'); return; }
    try {
      setRegLoading(true);
      await portalApi.post(API_ENDPOINTS.PORTAL.REGISTER, {
        email: regEmail,
        password: regPassword,
        client_id: Number(regClientId),
        site_id: DEFAULT_SITE_ID,
      });
      setRegSuccess(true);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Registration failed';
      setRegError(msg);
    } finally {
      setRegLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 420, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <PetsIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography variant="h5" fontWeight={700}>Client Portal</Typography>
            <Typography variant="body2" color="textSecondary">
              View your pet's records and appointments
            </Typography>
          </Box>

          <Tabs value={tab} onChange={(_, v) => { setTab(v); clearError(); setRegError(''); }} sx={{ mb: 3 }}>
            <Tab label="Sign In" />
            <Tab label="Create Account" />
          </Tabs>

          {tab === 0 && (
            <Box component="form" onSubmit={handleLogin}>
              {error && <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>{error}</Alert>}
              <TextField
                fullWidth label="Email" type="email" margin="normal" required
                value={email} onChange={e => setEmail(e.target.value)} autoComplete="email"
              />
              <TextField
                fullWidth label="Password" type="password" margin="normal" required
                value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password"
              />
              <Button
                type="submit" fullWidth variant="contained" size="large"
                sx={{ mt: 2 }} disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
            </Box>
          )}

          {tab === 1 && (
            <Box component="form" onSubmit={handleRegister}>
              {regError && <Alert severity="error" sx={{ mb: 2 }}>{regError}</Alert>}
              {regSuccess ? (
                <Alert severity="success">
                  Account created! You can now sign in with your email and password.
                  <Button size="small" onClick={() => setTab(0)} sx={{ ml: 1 }}>Sign In</Button>
                </Alert>
              ) : (
                <>
                  <TextField
                    fullWidth label="Email" type="email" margin="normal" required
                    value={regEmail} onChange={e => setRegEmail(e.target.value)}
                  />
                  <TextField
                    fullWidth label="Password (min 8 characters)" type="password" margin="normal" required
                    value={regPassword} onChange={e => setRegPassword(e.target.value)}
                  />
                  <TextField
                    fullWidth label="Client ID" margin="normal" required
                    helperText="Your client ID is on any invoice or registration letter from the clinic"
                    value={regClientId} onChange={e => setRegClientId(e.target.value)}
                  />
                  <Button
                    type="submit" fullWidth variant="contained" size="large"
                    sx={{ mt: 2 }} disabled={regLoading}
                  >
                    {regLoading ? <CircularProgress size={24} /> : 'Create Account'}
                  </Button>
                </>
              )}
            </Box>
          )}

          <Divider sx={{ my: 3 }} />
          <Typography variant="caption" color="textSecondary" display="block" textAlign="center">
            Staff login? <a href="/login">Click here</a>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClientLoginPage;
